<template>
  <section
    class="section primary-header"
    :class="{ 'has-tabs': hasTabs(), 'is-platform-admin': usePlatformAdminHeading }"
  >
    <div class="container is-fluid">
      <div class="container__section">
        <div class="container__titles">
          <h1 class="title is-2">{{ title }}</h1>
          <h2 class="subtitle is-6">{{ description }}</h2>
        </div>
        <div class="container__buttons" v-if="hasButtons()">
          <div v-for="button in buttons" :key="button.content">
            <n-button :variant="button.variant" @click="button.clickHandler">{{ button.content }}</n-button>
          </div>
        </div>
      </div>
      <div class="tabs" v-if="hasTabs()">
        <ul>
          <li v-for="tab in tabs" :key="tab.name" :class="{ 'is-active': isTabActive(tab) }">
            <router-link :to="{ name: tab.route }">{{ tab.name }}</router-link>
          </li>
        </ul>
        <div>
          <slot name="tab-icons" />
        </div>
      </div>
    </div>
  </section>
</template>
<script lang="ts" setup>
import { TabItem } from './tabItem';
import { HeaderButton } from './headerButton';
import { useRoute, useRouter } from 'vue-router';
import { computed, PropType } from 'vue';
import { useAuth } from '@/authentication/useAuth';

const props = defineProps({
  title: {
    type: String,
  },
  description: {
    type: String,
  },
  tabs: {
    type: Array as PropType<TabItem[]>,
  },
  buttons: {
    type: Array as PropType<HeaderButton[]>,
  },
});

const { currentRoute, getRoutes } = useRouter();
const route = useRoute();
const { isPlatformAdmin } = useAuth();

const activeRouteName = computed(() => String(route?.name || ''));
const isPlatformAdminRoute = computed(() => activeRouteName.value.startsWith('platform-admin'));
const usePlatformAdminHeading = computed(() => isPlatformAdmin.value && isPlatformAdminRoute.value);

function hasTabs() {
  return props.tabs && props.tabs.length > 0;
}

function hasButtons() {
  return !!props.buttons && props.buttons.length > 0;
}

function isTabActive(tab: TabItem): boolean {
  return isTabTheActiveRoute(tab) || isNoExactMatchAndActiveRouteBeginsWithTabsPath(tab);
}

function isTabTheActiveRoute(tab: TabItem) {
  return activeRouteName.value === tab.route;
}

function isNoExactMatchAndActiveRouteBeginsWithTabsPath(tab: TabItem) {
  return !isAnyTabActive() && doesActiveRouteBeginWithPath(tab.route);
}

function isAnyTabActive(): boolean {
  return props.tabs.filter((tab: TabItem) => activeRouteName.value === tab.route).length > 0;
}

function doesActiveRouteBeginWithPath(routeName: string): boolean {
  const path: string = getRoutePath(routeName);
  return !!currentRoute.value?.path && currentRoute.value?.path.indexOf(path) === 0;
}

function getRoutePath(routeName: string): string {
  const allRoutes = getRoutes();
  const matchedRoutes = allRoutes.filter((route) => route.name === routeName || route.name === '/' + routeName);
  return matchedRoutes.length === 1 ? matchedRoutes[0].path : '';
}
</script>

<style scoped lang="scss">
.has-tabs {
  .subtitle {
    margin-bottom: 0;
  }
}

.tabs {
  position: absolute;
  margin-top: 5px;
  left: 0;
  right: 0;
  margin-left: 32px;
  margin-right: 32px;
  overflow: visible;

  ul {
    border-bottom-color: transparent;
  }

  a {
    color: rgba(255, 255, 255, 0.6);
    border-bottom-color: transparent;

    &:hover {
      color: #ffffff;
      border-bottom-color: #ffffff;
    }
  }

  li.is-active {
    a {
      color: $color-netcall-jonquil;
      border-bottom-color: $color-netcall-jonquil;
    }
  }
}
.container {
  &__section {
    display: flex;
    align-items: flex-end;
  }
  &__buttons {
    margin-left: auto;
  }
}
</style>
